
import { AbstractMapModule, LegendData, MapLibreLayerConfig, MapModuleStyle, ViewStackController } from "gertrud";
import { sidebarPanelNames } from "@/sidebarPanelNames";
import { MapModuleViewStackItem } from "@/MapModuleViewStackItem";
import { registerMapModule } from "./mapModuleRegistry";
import { AtlasMapModuleConfig } from "atlas-shared/dist/mapApp/mapModuleConfigs/AtlasMapModuleConfig";


/**
 * Extends {@link AbstractMapModule} with a `ViewStackController` reference so
 * that modules can push/pop sidebar panels using the gertrud breadcrumb navigation.
 *
 * In its constructor it also injects the module's MapLibre sources and layers from
 * `moduleConfig` into the shared `mapDef` object (the single MapLibre style object
 * shared across all modules in a map view).
 */

// ATTENTION: Do NOT make this class abstract! It is actually used, for example for base maps!

export class AtlasMapModule extends AbstractMapModule {

    declare moduleConfig: AtlasMapModuleConfig


    /** Layers whose id starts with `<uuid>_hover:` — used for hover highlighting. */
    protected hoverHighlightLayers: MapLibreLayerConfig[] = []

    /** Layers whose id starts with `<uuid>_select:` — used for select highlighting. */
    protected selectHighlightLayers: MapLibreLayerConfig[] = []

    /** The GeoJSON feature property that holds the feature ID. Defaults to `'id'`. Override in subclasses if needed (e.g. `'_id'` for IKM). */
    protected featureIdProperty: string = 'id'

    constructor(config: AtlasMapModuleConfig, protected vsc: ViewStackController) {
        super(config)
    }

    /**
     * Overrides the base `setStyle()` to automatically discover hover/select highlight
     * layers after the style is applied. Subclasses don't need to call
     * `discoverHighlightLayers()` manually — it happens here.
     */
    override setStyle(style: MapModuleStyle): void {
        super.setStyle(style)
        this.discoverHighlightLayers()
    }

    /**
     * Discovers hover and select highlight layers by scanning the active style for
     * layers whose IDs start with `<uuid>_hover:` and `<uuid>_select:` respectively.
     * Call this after every `setStyle()` invocation so the cached layer lists stay
     * in sync with the current style.
     */
    protected discoverHighlightLayers(): void {
        this.hoverHighlightLayers = this.findLayersWithPrefix(this.uuid + "_hover:")
        this.selectHighlightLayers = this.findLayersWithPrefix(this.uuid + "_select:")
    }

    /**
     * Returns the legend data of the module's active style, or `null` if the
     * active style carries no legend data.
     *
     * `MapView.vue` calls this for every module and aggregates the returned
     * legend groups into the map legend. Subclasses may override this to
     * customize the legend data (e.g. `RaumplanungMapModule` re-labels the
     * first group with the current indicator/time-frame).
     */
    getLegendData(): LegendData | null {

        const legendData = this.reactiveData.activeStyle?.legendData

        if (legendData) {
            const legendDataCopy = JSON.parse(JSON.stringify(legendData))
            // Only stamp the module opacity onto the legend items when legend
            // opacity sync is enabled (default). Set `syncLegendOpacity: false`
            // in the module config to keep legend symbols at full opacity.
            if (this.moduleConfig.syncLegendOpacity !== false) {
                return this.applyModuleOpacityToLegendData(legendDataCopy)
            }
            return legendDataCopy
        }

        return null
    }

    /**
     * Returns the attribution string(s) of this module's active style, or
     * `null` if the module carries no attribution.
     *
     * The attribution is collected from the `attribution` field of every
     * source in the active style (and, as a fallback, from the module config's
     * `attribution` field). Duplicate strings are removed. `MapView.vue` calls
     * this for every visible module and renders the combined result below the
     * legend. Subclasses may override this to customize the attribution
     * (e.g. `RaumplanungMapModule` reads it from the response style).
     */
    getAttribution(): string | null {
        const attributions = new Set<string>()

        const sources = this.reactiveData.activeStyle?.sources
        if (sources) {
            for (const source of Object.values(sources)) {
                const attr = source?.attribution
                if (attr && attr.trim().length > 0) {
                    attributions.add(attr.trim())
                }
            }
        }

        const configAttr = (this.moduleConfig as any)?.attribution
        if (configAttr && configAttr.trim().length > 0) {
            attributions.add(configAttr.trim())
        }

        if (attributions.size === 0) {
            return null
        }

        return Array.from(attributions).join(" ")
    }

    /**
     * Stamps the module's current opacity onto every legend item of the given
     * legend data. This is the single choke point through which all of a
     * module's legend data passes before reaching the `Legend` component, so
     * the legend symbols always reflect the module-level opacity.
     *
     * @param legendData - A (deep-copied) legend data object to modify in place.
     * @returns The same `legendData` object with each item's `opacity` set.
     */
    protected applyModuleOpacityToLegendData(legendData: LegendData): LegendData {
        const opacity = this.opacity
        for (const group of legendData.groups) {
            for (const item of group.items) {
                item.opacity = opacity
            }
        }
        return legendData
    }

    /**
     * Applies a MapLibre `["in", ...]` filter expression to every layer in `layers`.
     *
     * @param layers - The highlight layers to update (hover or select).
     * @param featureIds - The feature IDs to include. Pass an empty array to clear the highlight.
     */
    protected setHighlightFilter(
        layers: MapLibreLayerConfig[],
        featureIds: string[]
    ): void {
        const filter = ["in", ["get", this.featureIdProperty], ["literal", featureIds]]
        for (const layer of layers) {
            this.map?.setFilter(layer.id, filter)
        }
    }




    /**
     * Pushes this module's root sidebar component onto the ViewStack.
     * Subclasses override this to push their own specific component.
     */
    initSidebar(): void {
        const compName = this.moduleConfig.sidebarRootComponent || sidebarPanelNames.STANDARD_MODULE_MAIN

        const item: MapModuleViewStackItem = {
            componentName: compName,
            props: {
                viewStackController: this.vsc,
                mapModule: this
            },
            role: 'standard-module-details',
            id: '',
            title: this.moduleConfig.title
        }



        this.vsc.addItem(item)
    }

    /**
     * Called when the user clicks this module's entry in the map-modules sidebar list.
     * Makes the module visible and pushes its root sidebar component onto the ViewStack.
     */
    onModuleListEntryClicked(): void {
        this.visible = true
        this.initSidebar()
    }
}



registerMapModule("AtlasMapModule", AtlasMapModule);